import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (s) => (s || "").slice(0, 16).replace("T", " ");

const StrikeRow = ({ s, onRemove, busy }) => (
  <li className="p-3 rounded-lg border border-neutral-800 flex items-start gap-3" data-testid={`strike-row-${s.id}`}>
    <div className="flex-1 min-w-0">
      <div className="text-sm text-neutral-100" data-testid={`strike-reason-${s.id}`}>{s.reason}</div>
      <div className="text-xs text-neutral-500 mt-0.5">
        {fmt(s.created_at)}{s.order_ref && <> · pedido {s.order_id ? <a className="underline font-mono" href={`/order/${s.order_id}`} target="_blank" rel="noreferrer" data-testid={`strike-order-${s.id}`}>{String(s.order_ref).slice(0, 8)}</a> : <span className="font-mono">{String(s.order_ref).slice(0, 8)}</span>}</>}
        {s.removed_at && <> · quitado {fmt(s.removed_at)}: {s.remove_reason}</>}
      </div>
    </div>
    {onRemove && <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-500/40 text-emerald-300" disabled={busy} onClick={() => onRemove(s)} data-testid={`strike-remove-${s.id}`}>− Strike</Button>}
  </li>
);

export function UserStrikesDialog({ user, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/users/${user.id}/strikes`).then((r) => setData(r.data)).catch(() => setData({ strikes: [], removed: [] }));
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async (s) => {
    const reason = window.prompt(`Quitar el strike "${s.reason}" a ${user.email}.\nMotivo (obligatorio):`);
    if (!reason || reason.trim().length < 3) { if (reason !== null) toast.error("El motivo es obligatorio"); return; }
    if (!window.confirm("¿Confirmas quitar este strike? Queda en la auditoría.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/strikes/${s.id}/remove`, { reason: reason.trim() });
      toast.success(`Strike quitado · ahora tiene ${r.strikes}${r.unbanned ? " · ban automático levantado" : ""}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo quitar"); }
    finally { setBusy(false); }
  };
  const add = async () => {
    const reason = window.prompt(`Sumar un strike a ${user.email}.\nMotivo (obligatorio):`);
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try { await api.post("/admin/strikes", { user_id: user.id, reason: reason.trim() }); toast.success("Strike aplicado"); load(); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo aplicar"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl bg-[#0b0b0b] border-neutral-800" data-testid="user-strikes-dialog">
        <DialogHeader><DialogTitle>Strikes de {user.name || user.email}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-neutral-500">Cargando…</div> : (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span data-testid="user-strikes-count">Activos: <b>{data.strikes.length}</b> de {data.ban_threshold} (ban automático){data.user?.banned && <span className="ml-2 text-red-400">· baneado</span>}</span>
              <Button size="sm" variant="ghost" className="text-amber-400 h-7 text-xs" disabled={busy} onClick={add} data-testid="user-strikes-add">+ Strike</Button>
            </div>
            {data.strikes.length === 0 ? <div className="text-sm text-neutral-500" data-testid="user-strikes-empty">Sin strikes activos.</div> : (
              <ul className="space-y-2">{data.strikes.map((s) => <StrikeRow key={s.id} s={s} onRemove={remove} busy={busy} />)}</ul>
            )}
            {data.removed.length > 0 && (
              <div>
                <div className="text-xs text-neutral-500 mb-2">Quitados</div>
                <ul className="space-y-2 opacity-70">{data.removed.map((s) => <StrikeRow key={s.id} s={s} />)}</ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
